import { useEffect, useRef, useState } from "react";

import { CustomButton } from "../../components/CustomButton";
import { CreateNewTypeRow } from "../../components/assets/CreateNewTypeRow";
import { CustomTable } from "../../components/table/CustomTable";
import {
  dispatchAssetTypesAction,
  dispatchBulkDeleteTypesAction,
  dispatchCreateTypeAction,
} from "../../store/assets/actions";
import { RootState } from "../../store/store";
import { showErrorToast, showSuccessToast } from "../../utils/common";
import { useAppSelector } from "../../utils/hooks/store";
import { useTranslation } from "react-i18next";

const AdminTypesPage = () => {
  const { t } = useTranslation();
  const nameRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [createMode, setCreateMode] = useState<boolean>(false);
  const { types } = useAppSelector((state: RootState) => state.assets);

  const headers = [
    t("dashboard.types.headers.id"),
    t("dashboard.types.headers.name"),
    t("dashboard.types.headers.description"),
  ];
  const createHeaders = [t("dashboard.types.headers.name")];

  useEffect(() => {
    dispatchAssetTypesAction();
  }, []);

  const onRowSelect = (id: string) => {
    if (selected.includes(id)) setSelected(selected.filter((selectedId) => selectedId !== id));
    else setSelected([...selected, id]);
  };

  const onBulkSelect = () => {
    if (selected.length === types.length) setSelected([]);
    else setSelected(types.map((type) => type.id));
  };

  const onBulkDelete = () => {
    if (selected)
      dispatchBulkDeleteTypesAction(selected)
        .then(() => showSuccessToast("Types deleted successfully!"))
        .catch(() => {});
    setSelected([]);
  };

  const onToggleCreateMode = () => setCreateMode(!createMode);

  const onNewTypeSubmit = () => {
    const name: string = nameRef.current?.value ?? "";
    const description: string = descriptionRef.current?.value ?? "";

    if (!name) return showErrorToast({ type: "required" });

    dispatchCreateTypeAction({ name, description })
      .then(() => showSuccessToast("Asset type created successfully!"))
      .catch(() => {});
    onToggleCreateMode();
  };

  return (
    <div className="user-requests-container">
      <CustomTable
        data={types}
        className="mt-4"
        headers={headers}
        multiSelect={true}
        selected={selected}
        onSelect={onRowSelect}
        onBulkSelect={onBulkSelect}
      />
      {createMode && (
        <CreateNewTypeRow
          className="mb-4"
          inputRef={nameRef}
          headers={createHeaders}
          onSubmit={onNewTypeSubmit}
          descInputRef={descriptionRef}
        />
      )}
      <div className="d-flex justify-space-between">
        <div className="w-100 d-flex flex-column flex-lg-row gap-3">
          <CustomButton
            onClick={onToggleCreateMode}
            className="w-25 w-lg-100"
            styleType={createMode ? "secondary" : "success"}
            label={`${createMode ? t("dashboard.types.discard") : t("dashboard.types.create")} ${t("dashboard.types.newType")}`}
          />
          <CustomButton
            onClick={onBulkDelete}
            styleType="danger"
            className="w-25 w-lg-100"
            label={t("dashboard.types.deleteSelected")}
            disabled={selected.length <= 0}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminTypesPage;
